/**
 * Synchronises the sidebar navItems tree with the current URL pathname.
 *
 * When users arrive at a page via the deep-link gate (global search), the
 * sidebar has no click-handler context to expand the correct menus.  This
 * helper walks the three-level navItems tree, finds the item whose `redirect`
 * (or `url` fallback) matches `pathname`, and returns a new array with the
 * matched item and all its ancestors marked `isOpen: true, isActive: true`.
 * Every non-matching sibling at each level is reset to `isOpen: false,
 * isActive: false` so only one path is highlighted.
 *
 * Returns `null` when no match is found, signalling the caller that no update
 * is needed (avoids unnecessary re-renders).
 *
 * @param {Array}  navItems  The current sidebar items array (3-level tree).
 * @param {string} pathname  The current location pathname to match against.
 * @returns {Array|null}     Updated navItems or null if nothing matched.
 */
export default function syncNavToPath(navItems, pathname) {
    if (!navItems || !pathname) return null;

    // Normalise — strip trailing slash for comparison.
    const norm = (p) => (p && p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p);
    const target = norm(pathname);

    // Walk the tree to locate every exact match. Several sidebar leaves can
    // intentionally share an intermediate search URL; in that case retain
    // the active leaf chosen by the click handler instead of arbitrarily
    // selecting the first matching item. We still apply the selected path
    // below so stale/collapsed ancestors are normalized as well.
    const exactMatches = [];
    const collectExactMatches = (items, parentPath = []) => {
        items.forEach((item, index) => {
            const itemPath = [...parentPath, index];
            if (norm(item.redirect) === target || norm(item.url) === target) {
                exactMatches.push(itemPath);
            }
            if (Array.isArray(item.subLinks)) {
                collectExactMatches(item.subLinks, itemPath);
            }
        });
    };
    collectExactMatches(navItems);

    const activeLeafMatch = exactMatches.find((path) => {
        const item = path.reduce((current, index) => current?.subLinks?.[index], { subLinks: navItems });
        return item?.isActive && (!Array.isArray(item.subLinks) || item.subLinks.length === 0);
    });
    let matchPath = activeLeafMatch || exactMatches[0] || null;

    // Fallback: prefix-match for parameterised routes (e.g. the sidebar item
    // url is /student-life/health-record/immunizations but the actual pathname
    // is /student-life/health-record/immunizations/edit/12345).  Pick the
    // deepest (longest-url) item whose path is a proper prefix of the target.
    if (!matchPath) {
        let bestLen = 0;
        const isPrefix = (p) => {
            const n = norm(p);
            if (!n || n.length <= bestLen) return false;
            if (target.startsWith(n + '/')) {
                bestLen = n.length;
                return true;
            }
            return false;
        };

        for (let ri = 0; ri < navItems.length; ri++) {
            const root = navItems[ri];
            if (isPrefix(root.redirect) || isPrefix(root.url)) {
                matchPath = [ri];
            }
            const subs = root.subLinks;
            if (!subs) continue;
            for (let si = 0; si < subs.length; si++) {
                const sub = subs[si];
                if (isPrefix(sub.redirect) || isPrefix(sub.url)) {
                    matchPath = [ri, si];
                }
                const nested = sub.subLinks;
                if (!nested) continue;
                for (let ni = 0; ni < nested.length; ni++) {
                    const n = nested[ni];
                    if (isPrefix(n.redirect) || isPrefix(n.url)) {
                        matchPath = [ri, si, ni];
                    }
                    const deep = n.subLinks;
                    if (!deep) continue;
                    for (let di = 0; di < deep.length; di++) {
                        const d = deep[di];
                        if (isPrefix(d.redirect) || isPrefix(d.url)) {
                            matchPath = [ri, si, ni, di];
                        }
                    }
                }
            }
        }
    }

    if (!matchPath) return null;

    // Clone and apply isOpen / isActive along the matched path.
    const updated = navItems.map((root, ri) => {
        const isRootMatch = ri === matchPath[0];
        const newRoot = {
            ...root,
            isOpen: isRootMatch,
            isActive: isRootMatch && matchPath.length === 1
        };

        if (!root.subLinks) return newRoot;

        newRoot.subLinks = root.subLinks.map((sub, si) => {
            const isSubMatch = isRootMatch && si === matchPath[1];
            const newSub = {
                ...sub,
                isOpen: isSubMatch,
                isActive: isSubMatch && matchPath.length === 2
            };

            if (!sub.subLinks) return newSub;

            newSub.subLinks = sub.subLinks.map((nested, ni) => {
                const isNestedMatch = isSubMatch && ni === matchPath[2];
                const newNested = {
                    ...nested,
                    isOpen: isNestedMatch,
                    isActive: isNestedMatch && matchPath.length === 3
                };

                if (!nested.subLinks) return newNested;

                newNested.subLinks = nested.subLinks.map((deep, di) => {
                    const isDeepMatch = isNestedMatch && di === matchPath[3];
                    return {
                        ...deep,
                        isOpen: isDeepMatch,
                        isActive: isDeepMatch && matchPath.length === 4
                    };
                });

                return newNested;
            });

            return newSub;
        });

        return newRoot;
    });

    return updated;
}
